/**
 * SessionRow renders a single session entry in the Sessions tab timeline list.
 *
 * Comparison with SessionItem (src/components/psychologist/calendar/SessionItem.tsx):
 *   - SessionRow:    operates on SessionLite; shows patient avatar + initials,
 *     name, therapy type, "Próxima sesión" / "Primera vez" chips, a "Ver
 *     proceso" button, and a "Ingresar" (Google Meet) button for joinable
 *     sessions. Visual style: card row with border-radius, fixed height.
 *   - SessionItem:   operates on SessionWithPsychologist; is an absolutely
 *     positioned block inside the calendar grid, sized proportionally to session
 *     duration in minutes. Shows patient name, country flag, time, modality
 *     icon, and an unblock button for blocked slots. No shared action buttons.
 *
 * These components serve different contexts (list vs. calendar grid), accept
 * different data types, and share less than 40% of their logic. A shared base
 * component would add abstraction without meaningful code savings.
 */
import React from 'react';
import type { SessionLite } from '../../../../../types/session-lite';
import { Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import ProfileImage from '@/components/common/ProfileImage';
import { JoinSessionButton } from './JoinSessionButton';
import { getPaymentCountdown } from '../../../../../utils/paymentCountdown';
import CountryFlag from '@/components/common/CountryFlag';
import { BA_TIMEZONE, parseBackendSessionDate } from '../../../../../utils/timezone';
import { usePsychologistContext } from '../../../../../contexts/PsychologistContext';

type Props = {
  session: SessionLite;
  onViewSession: (session: SessionLite) => void;
  onAddNote?: (id: string) => void;
  isPast?: boolean;
  isNextJoinable?: boolean;
  hidePrimaryAction?: boolean;
};

/**
 * Normaliza timeLabel a formato "H:MM am/pm" (siempre con minutos).
 * "9 am" → "9:00 am"   /   "9:30 am" → "9:30 am"
 */
function formatTimeWithMinutes(raw?: string): string {
  if (!raw) return '';
  return raw.replace(/^(\d{1,2})\s+(am|pm)$/i, '$1:00 $2');
}

export const SessionRow: React.FC<Props> = ({ session, onViewSession, onAddNote: _onAddNote, isPast, isNextJoinable, hidePrimaryAction }) => {
  // Huso de la psicóloga desde el contexto (fuente única). Antes salía de
  // localStorage 'psi-timezone', que solo se escribe si alguna vez cambió el huso
  // en Perfil: sin esa escritura quedaba null y caía a la bandera de Argentina.
  const { psychologist } = usePsychologistContext();
  const nameExtras = (session as unknown as { lastName?: string; surname?: string });
  const lastName = nameExtras.lastName ?? nameExtras.surname ?? '';
  const fullName = lastName ? `${session.patientName} ${lastName}` : session.patientName;



  const getCancellationReasonEs = React.useCallback((raw?: string | null): string | null => {
    if (!raw) return null;
    const key = String(raw).trim().toLowerCase();
    const map: Record<string, string> = {
      'canceled because it corresponds to a free session': 'Cancelada por sesión sin cargo',
      'canceled by patient without reason and rescheduled': 'Paciente canceló sin motivo y reprogramó',
      'canceled by psychologist without reason and rescheduled': 'Profesional canceló sin motivo y reprogramó',
      'cancellation with payment in favor': 'Cancelada con pago a favor',
      'cancellation without payment': 'Cancelada sin pago',
      'cancelled by psychologist': 'Cancelada por profesional',
      'incorrectly scheduled': 'Agendada por error',
      'incorrectly scheduled and rescheduled': 'Agendada por error y reprogramada',
      'non payment': 'Falta de pago',
      'refund': 'Reembolso',
      'rescheduled': 'Reprogramada',
      'rescheduled by patient during out time': 'Reprogramada por paciente fuera de horario',
      'to be defined': 'A definir',
    };
    return map[key] || raw;
  }, []);

  return (
    <div id={`sess-${session.id}`} className={`sess-row ${session.joinable ? 'highlight' : ''} ${session.isCancelled ? 'cancelled' : ''} ${session.isNext ? 'next' : ''} ${isPast ? 'past' : ''}`}>
      <style>{`
        .sess-row { display:flex; align-items:center; justify-content:space-between; border:1px solid #e5e7eb; border-radius:14px; background:#fff; padding:12px 14px; gap:12px; position:relative; overflow:visible; }
        .sess-row.next { padding:12px 14px; border:2px solid #A08DDA; }
        .sess-row.cancelled { background:#fff; }
        .sess-row.past { background:#DCDCDC; }
        .sess-row.highlight::after { content:''; position:absolute; right:0; top:0; bottom:0; width:120px; }
        .sess-row .right-stack { display:flex; align-items:center; gap:8px; position:relative; z-index:1; }
        .sess-left { display:flex; align-items:center; gap:10px; }
        .meta { display:flex; flex-direction:column; gap:2px; }
        .name-row { display:flex; align-items:center; gap:8px; }
        .name { font-weight:700; color:#111827; }
        .meta-second { display:flex; align-items:center; gap:6px; flex-wrap:wrap; }
        .type { color:#6b7280; font-size:0.85rem; }
        .chips { display:flex; gap:6px; margin-bottom:0; }
        .sess-actions { display:flex; align-items:center; gap:8px; }
        .link { color:#6b7280; font-size:0.85rem; cursor:pointer; background:none; border:none; }
        @media (max-width: 1023px) {
          .sess-row { flex-direction: column; align-items: flex-start; gap: 8px; width: 100%; max-width: 100%; box-sizing: border-box; }
          .sess-left { width: 100%; align-items: flex-start; min-width: 0; }
          .meta { flex: 1; min-width: 0; }
          .name { font-size: 0.9375rem; }
          .meta-second { flex-wrap: wrap; gap: 4px; }
          .sess-row .right-stack { width: 100%; justify-content: stretch; }
          .sess-row .right-stack > * { flex: 1; min-width: 0; }
          .sess-row .right-stack button { width: 100% !important; white-space: normal; text-align: center; justify-content: center; min-height: 44px !important; height: auto !important; font-size: 0.875rem; line-height: 1.2; padding-top: 8px !important; padding-bottom: 8px !important; }
        }
      `}</style>
      <div className="sess-left">
        {session.timeLabel && (
          <div
            data-testid="session-row-time"
            className="flex items-center gap-1 text-sm font-semibold text-gray-700 whitespace-nowrap"
          >
            <CountryFlag timezone={psychologist?.timezone} fallbackIso2="AR" size="0.875rem" />
            <span data-testid="session-row-time-value">{formatTimeWithMinutes(session.timeLabel)}</span>
          </div>
        )}
        <ProfileImage
          src={session.patientAvatar}
          name={session.patientName}
          lastName={lastName}
          sizeRem={2.25}
        />
        <div className="meta">
          <div className="name-row">
            <Badge
              data-testid="session-therapy-type-badge"
              className="bg-gray-100 text-gray-600 text-[0.7rem] font-bold border border-gray-200 shrink-0"
            >
              {session.therapyType}
            </Badge>
            <span className="name">{fullName}</span>
          </div>
          <div className="meta-second">
            <div className="chips">
              {/* "Primera vez" prevalece: una sesión que es a la vez la próxima y la primera
                  del paciente muestra únicamente el chip "Primera vez". */}
              {session.isNext && !session.isFirst && (
                <Badge className="bg-[#30C48D] text-white text-[0.7rem] font-bold">Próxima sesión</Badge>
              )}
              {session.isFirst && (
                <Badge className="bg-[#6D57BC] text-white text-[0.7rem] font-bold">Primera vez</Badge>
              )}
              {/* Chip "Cancelada": reemplaza cualquier chip de pago para sesiones canceladas */}
              {session.isCancelled && (
                <Badge
                  data-testid="chip-cancelada"
                  className="bg-red-50 text-red-500 text-[0.7rem] font-bold border border-red-200"
                >
                  Cancelada
                </Badge>
              )}
              {/* Chip "Paga": sesión liquidada (pago recibido) */}
              {session.status === 'settled' && !session.isCancelled && (
                <Badge
                  data-testid="chip-paga"
                  className="bg-[#10B981] text-white text-[0.7rem] font-bold"
                >
                  Paga
                </Badge>
              )}
              {/* Chip "Pendiente de pago" + countdown inline */}
              {session.status === 'incomplete' && !session.isCancelled && (() => {
                const cd = getPaymentCountdown(parseBackendSessionDate(session.date, BA_TIMEZONE), BA_TIMEZONE);
                return (
                  <>
                    <Badge
                      data-testid="chip-pendiente-pago"
                      className="bg-[#FFEDD5] text-[#C2410C] text-[0.7rem] font-bold border border-[#FED7AA]"
                    >
                      Pendiente de pago
                    </Badge>
                    {!cd.isExpired && (
                      <span
                        data-testid="session-row-payment-countdown"
                        className={`inline-flex items-center gap-1 text-[0.7rem] font-medium ${cd.isUrgent ? 'text-orange-500' : 'text-amber-600'}`}
                      >
                        <span>⏱</span>
                        <span>{cd.countdownLabel}</span>
                        <Tooltip delayDuration={150}>
                          <TooltipTrigger asChild>
                            <span
                              data-testid="payment-policy-info-btn"
                              className="inline-flex items-center opacity-60 hover:opacity-100 cursor-default"
                              aria-label="Política de pago"
                            >
                              <Info size={11} />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="top" sideOffset={6} className="w-[280px] py-3 space-y-2 text-left leading-relaxed">
                            <p className="font-semibold text-foreground text-[0.8125rem]">Política de pago y cancelación</p>
                            <ul className="list-disc pl-4 space-y-1 text-xs">
                              <li>El pago debe realizarse hasta 12 horas antes del horario de la sesión.</li>
                              <li>Si no se paga en ese plazo, la sesión se cancela automáticamente.</li>
                              <li>Una sesión cancelada por falta de pago no puede realizarse el mismo día.</li>
                            </ul>
                          </TooltipContent>
                        </Tooltip>
                      </span>
                    )}
                  </>
                );
              })()}
            </div>
            {session.isCancelled && (() => {
              const reasonRaw = (session as unknown as { cancellationReason?: string }).cancellationReason;
              const reasonEs = getCancellationReasonEs(reasonRaw);
              return reasonEs ? <span className="type" style={{ color: '#7f1d1d' }}>{reasonEs}</span> : null;
            })()}
          </div>
        </div>
      </div>
      <div className={`right-stack ${session.joinable ? 'with-panel' : ''}`}>
        {!hidePrimaryAction && (
          <Button
            variant="outline"
            size="sm"
            style={{ borderRadius: '9999px', height: '35px', fontSize: '0.8125rem', borderColor: '#6D57BC', color: '#6D57BC', background: isPast ? '#DCDCDC' : 'transparent' }}
            onClick={() => onViewSession(session)}
          >
            Ver sesión
          </Button>
        )}

        <JoinSessionButton session={session} isNextJoinable={isNextJoinable} />
      </div>
    </div>
  );
};

export default SessionRow;
